import {Injectable, OnDestroy, inject} from '@angular/core';
import {Store} from '@ngrx/store';
import {ToastrService} from 'ngx-toastr';
import {BehaviorSubject, EMPTY, Observable, Subject, Subscription, combineLatest} from 'rxjs';
import {catchError, concatMap, distinctUntilChanged, map, tap} from 'rxjs/operators';
import {getLoggedInUser} from '@app/stores/authentication/authentication.selectors';
import {
  AppNavigation, Bookmark, MenuMode, PreferencesPatch, UserPreferences,
} from '@app/modules/shared/models/navigation';
import {
  PREFERENCE_LIMITS, bookmarkKey, cleanBookmarkLabel, normalizeInAppUrl, normalizePreferences,
} from '@app/_helpers/navigation-preferences';
import {preferencesOwnerId, readStoredCurrentUser, CURRENT_USER_KEY} from '@app/_helpers/stored-user';
import {normalizeNavigation} from '@app/_components/navigation/model/nav-tree';
import {PreferencesService} from '@app/services/preferences.service';
import {AuthenticationService} from '@app/services/authentication.service';

export type BookmarkResult = 'added' | 'removed' | 'limit' | 'invalid';

type PreferenceKey = keyof UserPreferences;

/**
 * The menus the server sent and the person's own preferences, live.
 *
 * Seeded from the signed-in user in the store, which every login-shaped response refreshes -
 * so starting or stopping an impersonation swaps the menu without a reload. The preferences
 * belong to the actor (preferencesOwnerId): an admin keeps their own layout and bookmarks
 * while looking through a customer's account, and a change of owner starts afresh.
 *
 * A change shows at once and is saved behind it, one request at a time and only the keys it
 * touched. A save that fails puts those keys back as the server last confirmed them. The
 * result is written into the stored user too, so a reload - or another tab, through the
 * storage event - picks it up.
 */
@Injectable({providedIn: 'root'})
export class NavigationStateService implements OnDestroy {
  private readonly store = inject(Store);
  private readonly preferencesService = inject(PreferencesService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly toastr = inject(ToastrService);

  private readonly navigationSubject = new BehaviorSubject<AppNavigation | null>(null);
  private readonly preferencesSubject = new BehaviorSubject<UserPreferences>(normalizePreferences(null));
  private readonly writes = new Subject<{epoch: number; keys: PreferenceKey[]}>();
  private readonly subscriptions = new Subscription();

  private ownerId: number | null = null;
  /** What the server last confirmed; a failed save rolls back to it. */
  private confirmed: UserPreferences = normalizePreferences(null);
  /** Keys with a save queued or in flight, and how many. */
  private readonly pending = new Map<PreferenceKey, number>();
  /** Bumped when the owner changes, so a save queued for the previous one is dropped. */
  private epoch = 0;
  private expandTimer: ReturnType<typeof setTimeout> | null = null;
  private lastFailureToastAt = 0;

  readonly navigation$: Observable<AppNavigation | null> = this.navigationSubject.asObservable();
  readonly preferences$: Observable<UserPreferences> = this.preferencesSubject.asObservable();
  /** Top unless the person chose side - and top whenever there is no menu to put in a sidebar. */
  readonly menuMode$: Observable<MenuMode> = combineLatest([this.navigation$, this.preferences$]).pipe(
    map(([navigation, preferences]) => (navigation ? preferences.menuMode : 'top') as MenuMode),
    distinctUntilChanged()
  );
  readonly bookmarks$: Observable<Bookmark[]> = this.preferences$.pipe(
    map((preferences) => preferences.bookmarks),
    distinctUntilChanged()
  );

  private readonly onStorage = (event: StorageEvent) => this.syncFromStorage(event);

  constructor() {
    this.subscriptions.add(this.store.select(getLoggedInUser).subscribe((user) => this.adopt(user)));
    this.subscriptions.add(this.writes.pipe(concatMap((write) => this.send(write))).subscribe());
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.onStorage);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.onStorage);
    }
    if (this.expandTimer) {
      clearTimeout(this.expandTimer);
    }
  }

  get preferences(): UserPreferences {
    return this.preferencesSubject.value;
  }

  get navigation(): AppNavigation | null {
    return this.navigationSubject.value;
  }

  setMenuMode(mode: MenuMode): void {
    if (this.preferences.menuMode !== mode) {
      this.change({menuMode: mode});
    }
  }

  setSidebarCollapsed(collapsed: boolean): void {
    if (this.preferences.sidebarCollapsed !== collapsed) {
      this.change({sidebarCollapsed: collapsed});
    }
  }

  /**
   * Open or close a menu group. Saved after a pause, since people open and close several in
   * a row; losing one to a reload inside that pause costs nothing.
   */
  setGroupExpanded(id: string, expanded: boolean): void {
    const groups = this.preferences.expandedGroups.filter((group) => group !== id);
    if (expanded) {
      groups.push(id);
    }
    this.change({expandedGroups: groups.slice(-PREFERENCE_LIMITS.expandedGroups)}, true);
  }

  isBookmarked(target: {menuId?: string; url?: string}): boolean {
    const key = this.targetKey(target);
    return !!key && this.preferences.bookmarks.some((bookmark) => bookmarkKey(bookmark) === key);
  }

  /** Star or unstar a menu entry that is the same wherever you are, optionally under a name of one's own. */
  toggleMenuBookmark(menuId: string, label?: string | null): BookmarkResult {
    if (!menuId) {
      return 'invalid';
    }
    const clean = cleanBookmarkLabel(label);
    return this.toggle({kind: 'menu', menuId, ...(clean ? {label: clean} : {})});
  }

  /**
   * Star or unstar a page by its address: any in-app page, and every entry that belongs to one
   * project, whose bookmark has to remember which project it was.
   */
  togglePageBookmark(url: string, label: string, icon?: string | null): BookmarkResult {
    const normalized = normalizeInAppUrl(url);
    if (!normalized) {
      return 'invalid';
    }
    return this.toggle({
      kind: 'page', url: normalized, label: cleanBookmarkLabel(label) || normalized,
      ...(icon ? {icon} : {})
    });
  }

  removeBookmark(key: string): void {
    const bookmarks = this.preferences.bookmarks.filter((bookmark) => bookmarkKey(bookmark) !== key);
    if (bookmarks.length !== this.preferences.bookmarks.length) {
      this.change({bookmarks});
    }
  }

  moveBookmark(key: string, delta: -1 | 1): void {
    const bookmarks = [...this.preferences.bookmarks];
    const index = bookmarks.findIndex((bookmark) => bookmarkKey(bookmark) === key);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= bookmarks.length) {
      return;
    }
    [bookmarks[index], bookmarks[target]] = [bookmarks[target], bookmarks[index]];
    this.change({bookmarks});
  }

  /** Any bookmark may be renamed; clearing a menu bookmark's name brings back the entry's own. */
  renameBookmark(key: string, label: string): void {
    const clean = cleanBookmarkLabel(label);
    const bookmarks = this.preferences.bookmarks.map((bookmark) => {
      if (bookmarkKey(bookmark) !== key) {
        return bookmark;
      }
      if (bookmark.kind === 'page') {
        return clean ? {...bookmark, label: clean} : bookmark;
      }
      const {label: _previous, ...entry} = bookmark;
      return clean ? {...entry, label: clean} : entry;
    });
    this.change({bookmarks});
  }

  /** The bookmark behind a key, if the person has one. */
  findBookmark(target: {menuId?: string; url?: string}): Bookmark | null {
    const key = this.targetKey(target);
    return key ? this.preferences.bookmarks.find((bookmark) => bookmarkKey(bookmark) === key) || null : null;
  }

  private targetKey(target: {menuId?: string; url?: string}): string | null {
    if (target.menuId) {
      return `menu:${target.menuId}`;
    }
    const url = normalizeInAppUrl(target.url);
    return url ? `page:${url}` : null;
  }

  private toggle(bookmark: Bookmark): BookmarkResult {
    const key = bookmarkKey(bookmark);
    const current = this.preferences.bookmarks;
    if (current.some((existing) => bookmarkKey(existing) === key)) {
      this.change({bookmarks: current.filter((existing) => bookmarkKey(existing) !== key)});
      return 'removed';
    }
    if (current.length >= PREFERENCE_LIMITS.bookmarks) {
      this.toastr.info(`You can keep up to ${PREFERENCE_LIMITS.bookmarks} favorites. Remove one to add another.`);
      return 'limit';
    }
    this.change({bookmarks: [...current, bookmark]});
    return 'added';
  }

  /** Show the change now, remember it, and queue the save. */
  private change(patch: PreferencesPatch, debounce = false): void {
    if (this.ownerId === null) {
      return;
    }
    const next = normalizePreferences({...this.preferences, ...patch});
    this.preferencesSubject.next(next);
    this.writeThrough(next);
    const keys = Object.keys(patch) as PreferenceKey[];
    const count = () => keys.forEach((key) => this.pending.set(key, (this.pending.get(key) || 0) + 1));
    if (!debounce) {
      count();
      this.writes.next({epoch: this.epoch, keys});
      return;
    }
    // A save still waiting out its pause is replaced, not added to: it sends the latest value.
    if (this.expandTimer) {
      clearTimeout(this.expandTimer);
    } else {
      count();
    }
    const epoch = this.epoch;
    this.expandTimer = setTimeout(() => {
      this.expandTimer = null;
      this.writes.next({epoch, keys});
    }, 800);
  }

  /** One save: the latest values of its keys, sent when its turn comes. */
  private send(write: {epoch: number; keys: PreferenceKey[]}): Observable<unknown> {
    if (write.epoch !== this.epoch) {
      return EMPTY;
    }
    const patch = write.keys.reduce((body, key) => ({...body, [key]: this.preferences[key]}), {} as PreferencesPatch);
    const settle = () => write.keys.forEach((key) => {
      const count = (this.pending.get(key) || 1) - 1;
      if (count > 0) {
        this.pending.set(key, count);
      } else {
        this.pending.delete(key);
      }
    });
    return this.preferencesService.update(patch).pipe(
      tap((saved) => {
        if (write.epoch !== this.epoch) {
          return;
        }
        settle();
        this.confirmed = saved;
        this.publish(this.withPending(saved));
      }),
      catchError((error) => {
        if (write.epoch === this.epoch) {
          settle();
          const rolledBack = {...this.preferences};
          write.keys.filter((key) => !this.pending.has(key))
            .forEach((key) => (rolledBack as any)[key] = this.confirmed[key]);
          this.publish(normalizePreferences(rolledBack));
          this.reportFailure(error);
        }
        return EMPTY;
      })
    );
  }

  /** The server's copy, with the keys still being saved kept as the person left them. */
  private withPending(incoming: UserPreferences): UserPreferences {
    const merged = {...incoming};
    this.pending.forEach((_count, key) => (merged as any)[key] = this.preferences[key]);
    return normalizePreferences(merged);
  }

  private publish(preferences: UserPreferences): void {
    this.preferencesSubject.next(preferences);
    this.writeThrough(preferences);
  }

  private writeThrough(preferences: UserPreferences): void {
    const stored = readStoredCurrentUser();
    if (stored && preferencesOwnerId(stored) === this.ownerId) {
      this.authenticationService.patchPersistedUser({preferences});
    }
  }

  private reportFailure(error: any): void {
    const now = Date.now();
    if (now - this.lastFailureToastAt < 10000) {
      return;
    }
    this.lastFailureToastAt = now;
    const message = error?.error?.message || 'Your menu settings could not be saved. Please try again.';
    this.toastr.warning(message);
  }

  /** A new session response, a hard reload, a sign-out or an impersonation switch. */
  private adopt(user: any): void {
    if (!user) {
      this.resetWrites();
      this.ownerId = null;
      this.confirmed = normalizePreferences(null);
      this.navigationSubject.next(null);
      this.preferencesSubject.next(normalizePreferences(null));
      return;
    }
    const navigation = normalizeNavigation(user.navigation);
    if (JSON.stringify(navigation) !== JSON.stringify(this.navigationSubject.value)) {
      this.navigationSubject.next(navigation);
    }
    const incoming = normalizePreferences(user.preferences);
    const owner = preferencesOwnerId(user);
    if (owner !== this.ownerId) {
      this.resetWrites();
      this.ownerId = owner;
      this.confirmed = incoming;
      this.preferencesSubject.next(incoming);
      return;
    }
    this.confirmed = incoming;
    const merged = this.withPending(incoming);
    if (JSON.stringify(merged) !== JSON.stringify(this.preferences)) {
      this.preferencesSubject.next(merged);
    }
  }

  /** Drop every save queued or waiting for the previous owner. */
  private resetWrites(): void {
    this.epoch++;
    this.pending.clear();
    if (this.expandTimer) {
      clearTimeout(this.expandTimer);
    }
    this.expandTimer = null;
  }

  /** Another tab saved preferences for the same person: take them, unless ours are mid-save. */
  private syncFromStorage(event: StorageEvent): void {
    if (event.key !== CURRENT_USER_KEY || !event.newValue || this.ownerId === null || this.pending.size) {
      return;
    }
    let stored: any = null;
    try {
      stored = JSON.parse(event.newValue);
    } catch (_) {
      return;
    }
    if (!stored || preferencesOwnerId(stored) !== this.ownerId) {
      return;
    }
    const incoming = normalizePreferences(stored.preferences);
    if (JSON.stringify(incoming) === JSON.stringify(this.preferences)) {
      return;
    }
    this.confirmed = incoming;
    this.preferencesSubject.next(incoming);
  }
}
